var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Renders a single MARC field instance from a FieldDef configuration.
 * Handles visible/hidden subfield partitioning with expand/collapse toggle.
 *
 * CZNA-1422 — one row per subfield occurrence, in the record's own order
 * (`utils/subfieldRowLayout.ts`), each with a ⋮ menu that moves it.
 *
 * CZNA-1410 — a subfield the template does not declare and the MARC catalog allows
 * can be added; such a subfield, added or loaded, is edited in place.
 *
 * CZNA-1449 — ONE control adds a subfield: the quiet last row "+ Přidat podpole…"
 * (`SubfieldCombobox`), offering the template's optional subfields first and the
 * catalog's after them. Alt+Enter in a row, or "Vložit podpole za" in its ⋮, opens
 * the same combobox right after that row and puts the subfield there.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Datepicker, Icon } from '@folio/stripes/components';
import { useOkapiKy } from '@folio/stripes/core';
import { Controller } from 'react-hook-form';
import SubfieldWithComponents from '../fields/SubfieldWithComponents';
import ComponentRow from '../fields/ComponentRow';
import SubfieldInput from '../fields/SubfieldInput';
import ClearFieldButton from '../fields/ClearFieldButton';
import RecordPickerSubfield from '../fields/RecordPickerSubfield';
import LockedNamePart from '../fields/LockedNamePart';
import VocabularySuggestSubfield from '../fields/VocabularySuggestSubfield';
import MultiSelectVocabularySubfield from '../fields/MultiSelectVocabularySubfield';
import ExternalIdentifierLookupSubfield from '../fields/ExternalIdentifierLookupSubfield';
import OpenAtSourceLink from '../fields/OpenAtSourceLink';
import { resolveLabel } from './labelResolver';
import SubfieldRowMenu from './SubfieldRowMenu';
import SubfieldCombobox from './SubfieldCombobox';
import { useSubfieldCatalog } from './SubfieldCatalogContext';
import { subfieldInputId, subfieldDatalistId, componentInputId, componentDatalistId, subfieldRowMenuId, carriedSubfieldInputId, addSubfieldMenuId, insertSubfieldComboboxId, } from './domIds';
import { subfieldRowStyle, subfieldCodeStyle, subfieldLabelStyle, subfieldInputStyle, subfieldReadOnlyInputStyle, subfieldActionsStyle, subfieldInputColumnOffset, subfieldLinkButtonStyle, } from '../fields/subfieldStyles';
import { readComponentSubfield, matchesVisibleWhen, visibilityInd1, visibleComponents } from '../codec/codecRegistry';
import DerivedComponentSubfield, { DerivedSubfieldSync, NAME_SUBFIELD, hasNameValue } from '../fields/DerivedComponentSubfield';
import { lookupComponentValidator } from '../codec/componentValidatorRegistry';
import { getActionHandler } from '../actions/actionHandlers';
import fillUriFromIdentifier from '../utils/fillUriFromIdentifier';
import { withMeetingOrdinalPeriod } from '../utils/meetingOrdinal';
import { isMultiValueSubfield } from '../utils/multiValueSubfield';
import { lockedNameParts } from '../utils/linkedHeadingCopy';
import { acceptsSuggestVocabulary } from '../utils/suggestVocabulary';
import { orderWithout, subfieldRowLayout } from '../utils/subfieldRowLayout';
import { emptySubfieldValue } from '../formTypes';
import { addableSubfields, addedTicket, writableCatalogSubfield } from '../utils/addableSubfields';
import { useHelpTextContext } from '../../HelpTextProvider';
import SubfieldHelpButton from '../../SubfieldHelpButton';
import { subfieldHelpKey } from '../../../hooks/useHelpTexts';
var subfieldHelpRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.25rem',
};
/** Fixed width so rows with and without a "?" or a ⋮ keep their inputs aligned. */
var subfieldHelpGutterStyle = {
    width: '4rem',
    flexShrink: 0,
    paddingTop: '0.25rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.25rem',
};
/** A carried row's value: shown like an input, but not one (CZNA-1422). */
var carriedValueStyle = __assign(__assign({}, subfieldReadOnlyInputStyle), { minWidth: 0, color: '#333', overflowWrap: 'anywhere' });
/** The datalist component sits in a block cell (`ComponentRow`), where `flex: 1` means nothing. */
var componentInputStyle = __assign(__assign({}, subfieldInputStyle), { width: '100%', boxSizing: 'border-box' });
var componentInputErrorStyle = __assign(__assign({}, componentInputStyle), { border: '1px solid #d0021b' });
var componentInputWarningStyle = __assign(__assign({}, componentInputStyle), { border: '1px solid #e09400' });
/** A component validator's message, under its input (CZNA-1572: it was `TextField`'s). */
var componentMessageStyle = function (level) { return ({
    color: level === 'error' ? '#d0021b' : '#9a6700',
    fontSize: '12px',
    marginTop: '2px',
}); };
/** An action under an input, from the input column (CZNA-1376) and inside the row's margin. */
var actionLinkRowStyle = {
    paddingLeft: subfieldInputColumnOffset,
    marginTop: '-4px',
    marginBottom: '8px',
};
var requiredAsteriskStyle = {
    color: '#d0021b',
    marginLeft: '2px',
};
/*
 * `matchesVisibleWhen` is imported, not defined here (CZNA-1007). It used to
 * be a private const, and `recordFormAffordance` carried a second copy that
 * had to be kept in step by hand — the save path and the renderer disagreeing
 * about which components are visible is exactly how `100$a` ended up with a
 * qualifier that was parsed and composed but never drawn.
 */
/**
 * Renders a subfield that has components (auto-composed, read-only value + editable components).
 */
var renderComponentSubfield = function (sf, pathPrefix, control, watch, intl, errors, currentInd1) {
    var ind1 = currentInd1 || '';
    // TASK-115: filter components by their `visibleWhen.ind1` predicate so the
    // `$a` row renders FAMILY_NAME + GIVEN_NAME for a surname-led heading and
    // FAMILY_NAME + FAMILY_QUALIFIER for a family group (CZNA-1007).
    var components = visibleComponents(sf, ind1);
    // Watch component values to compute summarized value.
    // Deep paths not statically resolvable — cast via unknown.
    var watchAny = watch;
    var componentValues = {};
    for (var _i = 0, components_1 = components; _i < components_1.length; _i++) {
        var comp = components_1[_i];
        var path = "".concat(pathPrefix, ".").concat(sf.code, ".components.").concat(comp.componentType);
        // eslint-disable-next-line react-hooks/rules-of-hooks -- components are static per config
        var val = watchAny(path);
        componentValues[comp.componentType] = val || '';
    }
    // CZNA-898 — asked the SAME way the save decides what to persist. Reading
    // only the affix here was this ticket's first attempt and it was wrong
    // wherever the affix is underivable: the panel composed the bare core while
    // an unedited save emitted the stored row.
    /* eslint-disable react-hooks/rules-of-hooks -- static per config, as above */
    var originals = {
        importedValue: watchAny("".concat(pathPrefix, ".").concat(sf.code, ".importedValue")),
        persisted: watchAny("".concat(pathPrefix, ".").concat(sf.code, ".persisted")),
    };
    /* eslint-enable react-hooks/rules-of-hooks */
    var summarizedValue = readComponentSubfield(sf, componentValues, originals, ind1);
    return (React.createElement(SubfieldWithComponents, { key: sf.code, code: sf.code, label: resolveLabel(sf, intl), value: summarizedValue, required: sf.required }, components.map(function (comp, idx) {
        var isFirstRequired = sf.required && idx === 0;
        var compLabel = isFirstRequired
            ? (React.createElement("span", null,
                resolveLabel(comp, intl),
                React.createElement("span", { style: requiredAsteriskStyle }, "*")))
            : resolveLabel(comp, intl);
        var compInputId = componentInputId(pathPrefix, sf.code, comp.componentType);
        return (React.createElement(ComponentRow, { key: comp.componentType, inputId: compInputId, label: compLabel },
            React.createElement(Controller, { name: "".concat(pathPrefix, ".").concat(sf.code, ".components.").concat(comp.componentType), control: control, render: function (_a) {
                    var field = _a.field;
                    // CZNA-962: a component input used to carry no handle at all —
                    // `100$a`'s Příjmení / Jméno were reachable only by their Czech
                    // label, which lives in `semantic-catalog.json` and moves.
                    //
                    // `key` matters as much as `id` here, and only for the Stripes
                    // input left (the Datepicker; the text components were a Stripes
                    // `TextField` until CZNA-1572). `TextField` copies `props.id` into `this.testId` in its
                    // CONSTRUCTOR (stripes-components 13.0.9, TextField.js:214) and
                    // never looks at the prop again, so a live instance whose id prop
                    // changes keeps rendering the old one. That happens on every
                    // removal from the middle of a repeatable field: `useFieldArray`
                    // re-indexes the survivors, React keeps their component instances
                    // (the panel is keyed by the RHF row id, which does not change),
                    // and the instance that was at index 2 then renders
                    // `sf-fields-400-1-b` for its plain subfields — those are our own
                    // `SubfieldInput`, a function component that recomputes — next to
                    // `sf-fields-400-2-a-FAMILY_NAME` for its components. One field
                    // instance, two different indices in its own DOM. Measured on the
                    // real bundle, not inferred. Keying on the id remounts exactly when
                    // the id changes and never otherwise.
                    //
                    // CZNA-1376: no input below gets a `label` of its own any more —
                    // `ComponentRow` draws it in the label column and points it at
                    // `compInputId`, which is why every branch must keep that id.
                    if (comp.componentInputType === 'select' && comp.selectOptions) {
                        // A datalist, NOT a `<select>` (CZNA-1007). The list is the
                        // closed set NK uses — `rod`, `rodina` — but 23 of the 2 684
                        // family headings in the corpus carry something else, and a
                        // real `<select>` cannot hold a value it has no option for:
                        // opening such a record would silently rewrite the qualifier
                        // to the first option (or to empty) on the next save.
                        //
                        // Hand-rolled rather than `TextField`, for the same reason
                        // the subfield-level combobox below is: Stripes' TextField
                        // owns its own `<input>` and gives no way to put a `list`
                        // attribute on it.
                        var listId = componentDatalistId(pathPrefix, sf.code, comp.componentType);
                        return (React.createElement(React.Fragment, null,
                            React.createElement("input", { id: compInputId, type: "text", list: listId, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, placeholder: comp.placeholder, style: componentInputStyle }),
                            React.createElement("datalist", { id: listId }, comp.selectOptions.map(function (opt) { return (
                            // The value IS the label when the config names
                            // none — `personal.json` ships `{"value": "rod"}`
                            // bare, because `rod`/`rodina` are the MARC terms
                            // themselves and not translatable UI text.
                            React.createElement("option", { key: opt.value, value: opt.value }, resolveLabel(opt, intl, opt.value))); }))));
                    }
                    if (comp.componentInputType === 'date') {
                        // Stripes Datepicker onChange args: (event, displayValue, isoBackendValue).
                        // `isoBackendValue` is empty when user clears, so fall back to
                        // displayValue then ''. No `error` rendering yet — date
                        // components are not currently sf.required; revisit when added.
                        return (React.createElement(Datepicker, { key: compInputId, id: compInputId, value: field.value || '', onChange: function (_e, displayValue, isoBackendValue) {
                                return field.onChange(isoBackendValue || displayValue || '');
                            }, onBlur: field.onBlur, backendDateStandard: "YYYY-MM-DD", marginBottom0: true }));
                    }
                    // Required-field error from form-level validation (e.g. familyName missing).
                    var requiredError = isFirstRequired && (errors === null || errors === void 0 ? void 0 : errors["".concat(sf.code, ".").concat(comp.componentType)])
                        ? intl.formatMessage({ id: "ui-czna-authorities.".concat(errors["".concat(sf.code, ".").concat(comp.componentType)]) })
                        : undefined;
                    // Inline component-level validator (TASK-092), resolved by
                    // name against the registry (CZNA-371) instead of a literal
                    // `=== 'yearQualifier'` test, so a new validator is a
                    // registry entry rather than a renderer edit. Runs on every
                    // render against the live value; pure function, no side
                    // effects. An unknown name resolves to null — the component
                    // renders unvalidated and the registry logs once.
                    var validatorError;
                    var validatorWarning;
                    var componentValidator = requiredError
                        ? null
                        : lookupComponentValidator(comp.componentValidator);
                    if (componentValidator) {
                        var result = componentValidator(field.value || '');
                        if ((result === null || result === void 0 ? void 0 : result.level) === 'error') {
                            validatorError = intl.formatMessage({ id: "ui-czna-authorities.".concat(result.messageKey) });
                        }
                        else if ((result === null || result === void 0 ? void 0 : result.level) === 'warning') {
                            validatorWarning = intl.formatMessage({ id: "ui-czna-authorities.".concat(result.messageKey) });
                        }
                    }
                    // A plain input, not Stripes' `TextField`: that one draws its border on a
                    // wrapper and its own background, so it never matched the subfield inputs
                    // around it (CZNA-1572). Its error/warning line is redrawn here.
                    var error = requiredError !== null && requiredError !== void 0 ? requiredError : validatorError;
                    var message = error !== null && error !== void 0 ? error : validatorWarning;
                    var messageId = "".concat(compInputId, "-message");
                    var inputStyle = componentInputStyle;
                    if (error)
                        inputStyle = componentInputErrorStyle;
                    else if (validatorWarning)
                        inputStyle = componentInputWarningStyle;
                    return (React.createElement(React.Fragment, null,
                        React.createElement("input", { id: compInputId, type: "text", value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, placeholder: comp.placeholder, 
                            // What Stripes' TextField set by default: no browser history
                            // or correction over a name part or a year.
                            autoComplete: "off", autoCorrect: "off", autoCapitalize: "off", spellCheck: false, "aria-invalid": !!error, "aria-describedby": message ? messageId : undefined, style: inputStyle }),
                        message && (React.createElement("div", { id: messageId, role: "alert", style: componentMessageStyle(error ? 'error' : 'warning') }, message))));
                } })));
    })));
};
/**
 * Subfield input rendered with a trailing action button (TASK-044).
 *
 * Owns its own loading state and resolves `useOkapiKy` at render time.
 * IMPORTANT: `okapiKy` is intentionally captured in the click closure and
 * NEVER listed in any hook dependency array — `useOkapiKy()` returns an
 * unstable reference and listing it triggers infinite refetch loops.
 */
var ActionButtonSubfield = function (_a) {
    var sf = _a.sf, fieldPath = _a.fieldPath, inputId = _a.inputId, pathPrefix = _a.pathPrefix, control = _a.control, intl = _a.intl, getValues = _a.getValues, setValue = _a.setValue, showToast = _a.showToast;
    var okapiKy = useOkapiKy();
    var _b = useState(false), isRunning = _b[0], setIsRunning = _b[1];
    var action = sf.actionButton;
    var handleClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        var handler;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    handler = getActionHandler(action.handler);
                    if (!handler || !getValues || !setValue || !showToast)
                        return [2 /*return*/];
                    setIsRunning(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, handler({
                            pathPrefix: pathPrefix,
                            getValues: getValues,
                            setValue: setValue,
                            intl: intl,
                            okapiKy: okapiKy,
                            showToast: showToast,
                        })];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    setIsRunning(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement(Controller, { name: fieldPath, control: control, render: function (_a) {
            var field = _a.field;
            return (React.createElement("div", null,
                React.createElement("div", { style: __assign(__assign({}, subfieldRowStyle), { alignItems: 'center' }) },
                    !sf.formOnly && React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
                        "$",
                        sf.code),
                    React.createElement("label", { htmlFor: inputId, style: subfieldLabelStyle },
                        resolveLabel(sf, intl),
                        sf.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' }, "aria-hidden": "true" }, "*")),
                    React.createElement("input", { id: inputId, type: "text", style: subfieldInputStyle, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, placeholder: sf.placeholder }),
                    React.createElement("span", { style: subfieldActionsStyle },
                        React.createElement(ClearFieldButton, { visible: !!field.value, onClear: function () { return field.onChange(''); } }))),
                React.createElement("div", { style: actionLinkRowStyle },
                    React.createElement("button", { type: "button", style: subfieldLinkButtonStyle, onClick: handleClick, disabled: isRunning, "aria-busy": isRunning }, isRunning ? React.createElement(Icon, { icon: "spinner-ellipsis" }) : resolveLabel(action, intl)))));
        } }));
};
/**
 * Renders a simple (non-component) subfield input.
 */
var renderSimpleSubfield = function (sf, pathPrefix, control, intl, getValues, setValue, showToast, siblingSubfields, fieldDef, nameLocked) {
    // Deterministic ID derived from pathPrefix + code — stable across renders,
    // unique per subfield. Shape and rationale: `renderer/domIds.ts`.
    //
    // CZNA-1048: computed HERE, above the component branches, not below them. It
    // used to sit just before the plain-input renderers, so the chip-list branch
    // right after this comment returned before the id existed and the chip list
    // was the one row on the form with no `#sf-` handle at all — leaving
    // `#sf-fields-100-0-c` matching nothing, and every assertion over it vacuous.
    var inputId = subfieldInputId(pathPrefix, sf.code);
    // CZNA-1474 — a part of a linked work's name is the authority's, shown and not edited.
    if (nameLocked) {
        return React.createElement(LockedNamePart, { key: sf.code, subfieldDef: sf, pathPrefix: pathPrefix, control: control, inputId: inputId });
    }
    // `multi-record-picker` (multi AUTHORITY, CZNA-214), `multi-vocabulary`
    // (multi VOCABULARY) and `multi-text` (free text, TASK-110) all render the
    // chip-list multi picker; the legacy `multiValue` flag covers hand-authored
    // configs. CZNA-567: the predicate is shared with the codec on purpose —
    // this branch and the codec's disagreeing is what made a chip list persist
    // nothing at all.
    if (isMultiValueSubfield(sf)) {
        return (React.createElement(MultiSelectVocabularySubfield, { key: sf.code, subfieldDef: sf, pathPrefix: pathPrefix, control: control, inputId: inputId }));
    }
    if (sf.inputType === 'vocabulary' || sf.inputType === 'record-picker') {
        return (React.createElement(RecordPickerSubfield, { key: sf.code, subfieldDef: sf, pathPrefix: pathPrefix, control: control, fieldDef: fieldDef, getValues: getValues, setValue: setValue }));
    }
    // CZNA-367: MARC 024 identifier lookup (ISNI/Wikidata/ORCID). Needs
    // getValues (to read 100's name components) and setValue (to fill $1) —
    // falls through to the plain input below when a caller renders read-only
    // (neither accessor supplied), same guard shape as the actionButton branch.
    if (sf.inputType === 'external-identifier-lookup' && getValues && setValue) {
        return (React.createElement(ExternalIdentifierLookupSubfield, { key: sf.code, sf: sf, pathPrefix: pathPrefix, control: control, inputId: inputId, getValues: getValues, setValue: setValue, siblingSubfields: siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], sourceLabel: fieldDef ? resolveLabel(fieldDef, intl) : undefined }));
    }
    var fieldPath = "".concat(pathPrefix, ".").concat(sf.code, ".value");
    if (sf.inputType === 'select' && sf.selectOptions) {
        return (React.createElement(Controller, { key: sf.code, name: fieldPath, control: control, render: function (_a) {
                var field = _a.field;
                return (React.createElement("div", { style: subfieldRowStyle },
                    !sf.formOnly && React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
                        "$",
                        sf.code),
                    React.createElement("label", { htmlFor: inputId, style: subfieldLabelStyle }, resolveLabel(sf, intl)),
                    React.createElement("select", { id: inputId, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, style: __assign(__assign({}, subfieldInputStyle), { backgroundColor: '#fff' }) },
                        React.createElement("option", { value: "" }, intl.formatMessage({ id: 'ui-czna-authorities.select.clear' })),
                        sf.selectOptions.map(function (opt) { return (React.createElement("option", { key: opt.value, value: opt.value }, resolveLabel(opt, intl))); })),
                    React.createElement("span", { style: subfieldActionsStyle },
                        React.createElement(ClearFieldButton, { visible: !!field.value, onClear: function () { return field.onChange(''); } }))));
            } }));
    }
    if (sf.inputType === 'combobox' && sf.selectOptions) {
        var datalistId_1 = subfieldDatalistId(pathPrefix, sf.code);
        return (React.createElement(Controller, { key: sf.code, name: fieldPath, control: control, render: function (_a) {
                var field = _a.field;
                return (React.createElement("div", { style: subfieldRowStyle },
                    !sf.formOnly && React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
                        "$",
                        sf.code),
                    React.createElement("label", { htmlFor: inputId, style: subfieldLabelStyle }, resolveLabel(sf, intl)),
                    React.createElement("input", { id: inputId, type: "text", list: datalistId_1, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, placeholder: sf.placeholder, style: subfieldInputStyle }),
                    React.createElement("datalist", { id: datalistId_1 }, sf.selectOptions.map(function (opt) { return (React.createElement("option", { key: opt.value, value: opt.value }, resolveLabel(opt, intl))); })),
                    React.createElement("span", { style: subfieldActionsStyle },
                        React.createElement(ClearFieldButton, { visible: !!field.value, onClear: function () { return field.onChange(''); } }))));
            } }));
    }
    // CZNA-718 — `derivedFrom` implies read-only here too. Only the components form has a
    // renderer of its own; a derived plain subfield would otherwise fall through to an
    // editable input that the server silently overwrites on save.
    if (sf.readOnly || sf.derivedFrom) {
        return (React.createElement(Controller, { key: sf.code, name: fieldPath, control: control, render: function (_a) {
                var field = _a.field;
                return (React.createElement(SubfieldInput, { code: sf.code, inputId: inputId, label: resolveLabel(sf, intl), value: field.value || '', disabled: true }));
            } }));
    }
    if (sf.inputType === 'textarea') {
        return (React.createElement(Controller, { key: sf.code, name: fieldPath, control: control, render: function (_a) {
                var field = _a.field;
                return (React.createElement("div", { style: __assign(__assign({}, subfieldRowStyle), { alignItems: 'flex-start' }) },
                    !sf.formOnly && React.createElement("span", { style: __assign(__assign({}, subfieldCodeStyle), { paddingTop: '8px' }), "aria-hidden": "true" },
                        "$",
                        sf.code),
                    React.createElement("label", { htmlFor: inputId, style: __assign(__assign({}, subfieldLabelStyle), { paddingTop: '8px' }) }, resolveLabel(sf, intl)),
                    React.createElement("textarea", { id: inputId, value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: field.onBlur, rows: 3, style: __assign(__assign({}, subfieldInputStyle), { fontSize: '14px', lineHeight: '1.4', fontFamily: 'inherit', resize: 'vertical' }) }),
                    React.createElement("span", { style: __assign(__assign({}, subfieldActionsStyle), { paddingTop: '2px' }) },
                        React.createElement(ClearFieldButton, { visible: !!field.value, onClear: function () { return field.onChange(''); } }))));
            } }));
    }
    // TASK-044: subfield with a trailing action button (e.g. "Fetch title" on $u).
    // Routed before the default plain-input branch so the action button is
    // rendered alongside the input.
    if (sf.actionButton) {
        return (React.createElement(ActionButtonSubfield, { key: sf.code, sf: sf, fieldPath: String(fieldPath), inputId: inputId, pathPrefix: pathPrefix, control: control, intl: intl, getValues: getValues, setValue: setValue, showToast: showToast }));
    }
    // CZNA-196 / CZNA-1467: $a blur keeps the sibling URI subfield ($1) in step
    // with $a when the field group declares a `uriTemplate` on it (config-driven,
    // no online lookup).
    var hasUriSibling = sf.code === 'a' && !!getValues && !!setValue
        && (siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : []).some(function (s) { return s.code !== 'a' && !!s.uriTemplate; });
    // CZNA-1614 — the same group template also lets the cataloguer open the typed
    // identifier at its source. Independent of the accessors: it only reads.
    var opensAtSource = sf.code === 'a'
        && (siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : []).some(function (s) { return s.code !== 'a' && !!s.uriTemplate; });
    // CZNA-1471 — the plain text input with a vocabulary quick pick (670$a source citations).
    // It stands in for the input below, so it keeps the required mark and the URI sibling.
    if (sf.suggestVocabularyType && acceptsSuggestVocabulary(sf)) {
        return (React.createElement(VocabularySuggestSubfield, { key: sf.code, subfieldDef: sf, pathPrefix: pathPrefix, control: control, inputId: inputId, onBlurValue: function (value) {
                if (hasUriSibling)
                    fillUriFromIdentifier(value, siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], pathPrefix, getValues, setValue);
            }, onClear: function () {
                if (hasUriSibling)
                    fillUriFromIdentifier('', siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], pathPrefix, getValues, setValue);
            } }));
    }
    return (React.createElement(Controller, { key: sf.code, name: fieldPath, control: control, render: function (_a) {
            var field = _a.field;
            return (React.createElement(SubfieldInput, { code: sf.code, inputId: inputId, label: resolveLabel(sf, intl), value: field.value || '', onChange: function (e) { return field.onChange(e.target.value); }, onBlur: function () {
                    field.onBlur();
                    // CZNA-1469: a meeting's number is stored with its full stop.
                    var typed = field.value || '';
                    var ordinal = withMeetingOrdinalPeriod(fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.tag, sf.code, typed);
                    if (ordinal !== typed)
                        field.onChange(ordinal);
                    if (hasUriSibling) {
                        fillUriFromIdentifier(field.value || '', siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], pathPrefix, getValues, setValue);
                    }
                }, placeholder: sf.placeholder, required: sf.required, inputAdornment: opensAtSource ? (React.createElement(OpenAtSourceLink, { code: sf.code, identifier: field.value || '', siblingSubfields: siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], sourceLabel: fieldDef ? resolveLabel(fieldDef, intl) : undefined })) : undefined, onClear: function () {
                    field.onChange('');
                    // CZNA-1467: clearing $a clears the URI derived from it as well.
                    if (hasUriSibling) {
                        fillUriFromIdentifier('', siblingSubfields !== null && siblingSubfields !== void 0 ? siblingSubfields : [], pathPrefix, getValues, setValue);
                    }
                } }));
        } }));
};
var DynamicFieldPanel = function (_a) {
    var _b, _c, _d;
    var fieldDef = _a.fieldDef, pathPrefix = _a.pathPrefix, control = _a.control, watch = _a.watch, getValues = _a.getValues, setValue = _a.setValue, showToast = _a.showToast, errors = _a.errors;
    var intl = useIntl();
    var panelRef = useRef(null);
    // A subfield revealed from the combobox lives in a ref (below); this re-renders after it.
    var _f = useState(0), setRevealed = _f[1];
    // TASK-115: watch the live `_ind1.value` of this field-instance so that
    // per-subfield / per-component `visibleWhen.ind1` predicates re-evaluate
    // when the user changes the heading-type selector. Deep paths are not
    // statically resolvable — cast via unknown.
    var watchAny = watch;
    var liveInd1 = visibilityInd1(fieldDef, watchAny("".concat(pathPrefix, "._ind1.value")));
    // Filter out structurally hidden subfields (e.g., $2 with defaultValue)
    // and apply TASK-115 `visibleWhen.ind1` predicate so heading-type-specific
    // subfields ($c, $d) appear only in their target mode. The same predicate
    // runs one level down on components (`visibleComponents`), which is where
    // the family qualifier is gated since CZNA-1007.
    var renderableSubfields = fieldDef.subfields
        .filter(function (sf) { return !sf.hidden; })
        .filter(function (sf) { return matchesVisibleWhen(sf.visibleWhen, liveInd1); });
    // CZNA-1422 — the rows, one per occurrence, in the record's order. An optional subfield
    // that holds a value is drawn too: it has a place in that order, and a row that is not
    // drawn cannot be moved past. Once drawn it stays drawn — emptying it while typing must
    // not pull the input from under the cursor. CZNA-1449 — an optional subfield is otherwise
    // drawn once the cataloguer adds it from the combobox.
    var instance = watchAny(pathPrefix);
    var carried = watchAny("passthrough.byInstance.".concat((_c = (_b = instance === null || instance === void 0 ? void 0 : instance._ptId) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '__none'));
    var revealed = useRef(new Set());
    var shows = function (sf, filled) {
        if (sf.hidden || !matchesVisibleWhen(sf.visibleWhen, liveInd1))
            return false;
        if (filled)
            revealed.current.add(sf.code);
        return sf.defaultVisible !== false || revealed.current.has(sf.code);
    };
    var layout = subfieldRowLayout(fieldDef, instance, carried, shows);
    // CZNA-1474 — the name parts a link on `$a` locks: not edited, and not added either.
    var locked = lockedNameParts(fieldDef, instance);
    // The optional subfields not drawn yet: the combobox's first suggestions (CZNA-1449).
    var collapsed = renderableSubfields.filter(function (sf) { return sf.defaultVisible === false && !revealed.current.has(sf.code)
        && !locked.includes(sf.code); });
    // CZNA-939 — the dates derived into `400 $d` are the dates of THIS occurrence's name, so
    // nothing is derived until the name is there. Computed once here (rather than in each half)
    // because the display and the mirror must never disagree about it.
    var nameFilled = hasNameValue(watch, pathPrefix, fieldDef.subfields.find(function (s) { return s.code === NAME_SUBFIELD; }));
    var renderSubfield = function (sf) {
        if (sf.hasComponents) {
            // CZNA-718 — a derived subfield (400 $d) reads its value off the field it derives
            // from and renders no inputs of its own. Routed before the editable branch so the
            // two can never both render: an editable copy of a derived value IS the second truth.
            if (sf.derivedFrom) {
                var visible = visibleComponents(sf, liveInd1);
                return (React.createElement(DerivedComponentSubfield, { sf: sf, components: visible, pathPrefix: pathPrefix, watch: watch, nameFilled: nameFilled }));
            }
            return renderComponentSubfield(sf, pathPrefix, control, watch, intl, errors, liveInd1);
        }
        return renderSimpleSubfield(sf, pathPrefix, control, intl, getValues, setValue, showToast, fieldDef.subfields, fieldDef, locked.includes(sf.code));
    };
    // The "?" to the right of every subfield row (CZNA-1339): where the value comes from and,
    // when NK wrote one, what the subfield means in this field (CZNA-1340). Done here, around
    // the row, because the subfield label itself is drawn in eight different components; the
    // fixed gutter keeps the inputs of one field aligned.
    var subfieldDescriptions = useHelpTextContext().subfieldDescriptions;
    var describedHelp = function (sf) {
        var help = subfieldDescriptions[subfieldHelpKey(fieldDef.tag, sf.code)];
        return help && help.description.trim() ? help : undefined;
    };
    // After a move the row is re-placed in the DOM, which drops the focus; give it back to
    // the moved row's ⋮, so the next move is one keystroke away. After an add, the focus
    // goes to the new row's first input, where the value is typed — a carried row's or a
    // template subfield's, whatever control draws it.
    var _g = useState(), focusAfterMove = _g[0], setFocusAfterMove = _g[1];
    var _h = useState(), focusAfterAdd = _h[0], setFocusAfterAdd = _h[1];
    useEffect(function () {
        var _a, _b, _c, _d, _f;
        if (!focusAfterMove)
            return;
        // After a removal the row named may have no ⋮ left; then the field's own combobox, and
        // with nothing left to add either, that row's first input.
        (_f = ((_b = (_a = document.getElementById(subfieldRowMenuId(pathPrefix, focusAfterMove))) !== null && _a !== void 0 ? _a : document.getElementById(addSubfieldMenuId(pathPrefix))) !== null && _b !== void 0 ? _b : (_d = (_c = panelRef.current) === null || _c === void 0 ? void 0 : _c.querySelector("[data-row-key=\"".concat(focusAfterMove, "\"]"))) === null || _d === void 0 ? void 0 : _d.querySelector('input:not([type="hidden"]):not([disabled]), textarea, select'))) === null || _f === void 0 ? void 0 : _f.focus();
        setFocusAfterMove(undefined);
    }, [focusAfterMove, pathPrefix]);
    useEffect(function () {
        var _a, _b, _c;
        if (!focusAfterAdd)
            return;
        (_c = (_b = (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-row-key=\"".concat(focusAfterAdd, "\"]"))) === null || _b === void 0 ? void 0 : _b.querySelector('input:not([type="hidden"]):not([disabled]), textarea, select')) === null || _c === void 0 ? void 0 : _c.focus();
        setFocusAfterAdd(undefined);
    }, [focusAfterAdd]);
    // CZNA-1410 — subfields the template does not declare live in the instance's passthrough
    // (CZNA-657), so that is where an added one goes and where an edit writes. Without a
    // catalog, or without `setValue`, the form offers nothing and edits nothing.
    var catalog = useSubfieldCatalog();
    var ticket = (_d = instance === null || instance === void 0 ? void 0 : instance._ptId) === null || _d === void 0 ? void 0 : _d.value;
    var editable = function (row) { return (!!setValue && !!ticket && !row.sf && !!writableCatalogSubfield(catalog, fieldDef.tag, row.code)
        && !locked.includes(row.code)); };
    var addable = setValue
        ? addableSubfields(catalog, fieldDef, (carried !== null && carried !== void 0 ? carried : []).map(function (r) { var _a; return (_a = r.subfield_code) !== null && _a !== void 0 ? _a : ''; }))
            .filter(function (s) { return !locked.includes(s.code); })
        : [];
    var templateOptions = collapsed.map(function (sf) { return ({ code: sf.code, label: resolveLabel(sf, intl) }); });
    var catalogOptions = addable.map(function (s) { return ({ code: s.code, label: resolveLabel({ label: s.label }, intl, "$".concat(s.code)) }); });
    var canAdd = templateOptions.length + catalogOptions.length > 0;
    /** Writes `_order` so the row `key` of the instance as it will be stands right after `anchor`. */
    var placeAfter = function (next, nextCarried, key, anchor) {
        if (!anchor || !setValue)
            return;
        var order = subfieldRowLayout(fieldDef, next, nextCarried, shows).placedAfter(key, anchor);
        if (order)
            setValue("".concat(pathPrefix, "._order"), { value: order }, { shouldDirty: true });
    };
    /** Adds `code` at its own place, or right after the row `anchor` ("Vložit podpole za"). */
    var add = function (code, anchor) {
        var _a, _b;
        if (collapsed.some(function (sf) { return sf.code === code; })) {
            // A template subfield: drawing its row is the add; the value typed into it is the data.
            revealed.current.add(code);
            placeAfter(instance, carried, code, anchor);
            setRevealed(function (n) { return n + 1; });
            setFocusAfterAdd(code);
            return;
        }
        if (!setValue || !getValues)
            return;
        var byInstance = ((_a = getValues('passthrough.byInstance')) !== null && _a !== void 0 ? _a : {});
        var target = ticket;
        if (!target) {
            // A fresh ticket, never one that is already a key: a deleted instance leaves its
            // bucket behind, and reusing that key would hand its rows to this instance.
            var n = 0;
            while (byInstance[addedTicket(n)])
                n += 1;
            target = addedTicket(n);
            setValue("".concat(pathPrefix, "._ptId"), { value: target }, { shouldDirty: true });
        }
        var rows = (_b = byInstance[target]) !== null && _b !== void 0 ? _b : [];
        /* eslint-disable camelcase -- ProposalDataField uses snake_case to match the API */
        var row = {
            field_tag: fieldDef.tag,
            field_index: 0, // re-stamped from the instance by the save
            subfield_code: code,
            // Past the highest index of the code, not its count: a saved row can stand at 1
            // alone once an empty 0 was dropped, and a count would number the next one 1 too.
            subfield_index: rows
                .filter(function (r) { return r.subfield_code === code; })
                .reduce(function (next, r) { var _a; return Math.max(next, ((_a = r.subfield_index) !== null && _a !== void 0 ? _a : 0) + 1); }, 0),
            value: '',
        };
        /* eslint-enable camelcase */
        setValue("passthrough.byInstance.".concat(target), __spreadArray(__spreadArray([], rows, true), [row], false), { shouldDirty: true });
        var key = "pt".concat(rows.length);
        placeAfter(__assign(__assign({}, instance), { _ptId: { value: target } }), __spreadArray(__spreadArray([], rows, true), [row], false), key, anchor);
        setFocusAfterAdd(key);
    };
    // CZNA-1449 — "Vložit podpole za": the combobox opened right after one row. `back` is
    // where Escape returns the focus: the input the shortcut came from, or the row's ⋮.
    var _j = useState(), insertAfter = _j[0], setInsertAfter = _j[1];
    var insertable = !!setValue && canAdd;
    /** Alt+Enter — free in Stripes (its keys are mod+s, alt+n, alt+c, mod+alt+…) and in the form. */
    var onRowKeyDown = function (key) { return function (e) {
        if (e.key !== 'Enter' || !e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || !insertable)
            return;
        e.preventDefault();
        setInsertAfter({ key: key, back: e.target });
    }; };
    var reorderable = !!setValue && layout.rows.length > 1;
    // CZNA-1474 — a locked name keeps its order too: neither its rows nor a row beside them
    // moves across it.
    var lockedRow = function (code) { return locked.length > 0 && (code === NAME_SUBFIELD || locked.includes(code)); };
    var moved = function (key, delta) {
        if (!reorderable)
            return undefined;
        var at = layout.rows.findIndex(function (r) { return r.key === key; });
        var across = layout.rows[at + delta];
        if (lockedRow(layout.rows[at].code) || (across && lockedRow(across.code)))
            return undefined;
        return layout.moved(key, delta);
    };
    var move = function (key, delta) {
        var order = moved(key, delta);
        if (!order || !setValue)
            return;
        setValue("".concat(pathPrefix, "._order"), { value: order }, { shouldDirty: true });
        setFocusAfterMove(key);
    };
    // CZNA-1515 — "Odebrat podpole": a row the cataloguer added, or a further occurrence of
    // a code. A row the template always draws, a mandatory, a derived or a locked one stays
    // and can only be emptied; so does a loaded code the catalog does not let anyone add
    // (`100 $7`), which no cataloguer added. A template subfield goes by emptying it — the
    // save drops an empty one — and folding its row back into the combobox; a carried row
    // leaves the passthrough. Offered only when the other rows keep their order.
    var removal = function (row) {
        var _a;
        if (!setValue || !instance || lockedRow(row.code))
            return undefined;
        var next = instance;
        var nextCarried = carried;
        if (row.kind === 'carried') {
            if (row.sf ? row.sf.derivedFrom : !editable(row))
                return undefined;
            nextCarried = (carried !== null && carried !== void 0 ? carried : []).filter(function (_, i) { return i !== row.index; });
        }
        else {
            var sf = row.sf;
            if (sf.defaultVisible !== false || sf.required || sf.derivedFrom)
                return undefined;
            next = __assign(__assign({}, instance), (_a = {}, _a[sf.code] = emptySubfieldValue(sf), _a));
        }
        var folded = row.kind === 'subfield' ? row.code : undefined;
        var showsAfter = function (sf, filled) { return (!sf.hidden && matchesVisibleWhen(sf.visibleWhen, liveInd1)
            && (sf.defaultVisible !== false || filled || (sf.code !== folded && revealed.current.has(sf.code)))); };
        var placed = orderWithout(fieldDef, layout, row.key, next, nextCarried, showsAfter);
        if (!placed)
            return undefined;
        return function () {
            var _a, _b;
            if (row.kind === 'carried') {
                setValue("passthrough.byInstance.".concat(ticket), nextCarried, { shouldDirty: true });
            }
            else {
                revealed.current.delete(row.code);
                setValue("".concat(pathPrefix, ".").concat(row.code), next[row.code], { shouldDirty: true });
            }
            if (placed.order !== undefined) {
                setValue("".concat(pathPrefix, "._order"), { value: placed.order }, { shouldDirty: true });
            }
            // The focus goes to the ⋮ of the row above, or below — a carried row after the removed
            // one is one index lower now, and so is its key — or, with no row left, the combobox.
            var at = layout.rows.indexOf(row);
            var neighbour = (_b = (_a = layout.rows[at - 1]) !== null && _a !== void 0 ? _a : layout.rows[at + 1]) !== null && _b !== void 0 ? _b : row;
            setRevealed(function (n) { return n + 1; });
            setFocusAfterMove(neighbour.kind === 'carried' && row.kind === 'carried' && neighbour.index > row.index
                ? "pt".concat(neighbour.index - 1) : neighbour.key);
        };
    };
    /** The name of a carried row: the template's for a declared code, else the catalog's. */
    var carriedLabel = function (row) {
        var _a, _b, _c, _d;
        if (row.sf)
            return resolveLabel(row.sf, intl, row.code);
        var entry = (_a = writableCatalogSubfield(catalog, fieldDef.tag, row.code)) !== null && _a !== void 0 ? _a : (_d = (_c = (_b = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _b === void 0 ? void 0 : _b[fieldDef.tag]) === null || _c === void 0 ? void 0 : _c.subfields) === null || _d === void 0 ? void 0 : _d.find(function (s) { return s.code === row.code; });
        return entry ? resolveLabel({ label: entry.label }, intl, "$".concat(row.code)) : "$".concat(row.code);
    };
    /**
     * A carried row (CZNA-1422): a further occurrence of a code, or a code the config does
     * not declare. A further occurrence has no control of its own — the form keys an
     * instance by code — so it is shown, not edited. A code the template does not declare
     * and the catalog allows is edited in place (CZNA-1410); emptied, the save drops it.
     * Either moves like any other row.
     */
    var renderCarried = function (row) {
        var _a, _b;
        if (editable(row)) {
            var valuePath_1 = "passthrough.byInstance.".concat(ticket, ".").concat(row.index, ".value");
            var change_1 = function (value) { return setValue(valuePath_1, value, { shouldDirty: true }); };
            return (React.createElement("div", { "data-carried-subfield": row.code },
                React.createElement(SubfieldInput, { code: row.code, inputId: carriedSubfieldInputId(pathPrefix, row.key), label: carriedLabel(row), value: (_a = row.row.value) !== null && _a !== void 0 ? _a : '', onChange: function (e) { return change_1(e.target.value); }, onBlur: function () {
                        var _a;
                        // CZNA-1469: a meeting's number added from the catalog gets its full stop too.
                        var typed = (_a = row.row.value) !== null && _a !== void 0 ? _a : '';
                        var ordinal = withMeetingOrdinalPeriod(fieldDef.tag, row.code, typed);
                        if (ordinal !== typed)
                            change_1(ordinal);
                    }, onClear: function () { return change_1(''); } })));
        }
        return (React.createElement("div", { style: subfieldRowStyle, "data-carried-subfield": row.code },
            React.createElement("span", { style: subfieldCodeStyle, "aria-hidden": "true" },
                "$",
                row.code),
            React.createElement("span", { style: subfieldLabelStyle }, carriedLabel(row)),
            React.createElement("div", { style: __assign(__assign({}, carriedValueStyle), { flex: 1 }), "data-carried-value": true }, (_b = row.row.value) !== null && _b !== void 0 ? _b : ''),
            React.createElement("span", { style: subfieldActionsStyle, "aria-hidden": "true" })));
    };
    /** Renders each subfield occurrence on its own full-width row, in the record's order. */
    // The menu is named by the code alone: its label would put the subfield's
    // name into a second accessible name, and every lookup of the input by its
    // label ("Název ($a)") would find the menu too. A code drawn more than once
    // (`382 $a housle … $a viola`) names each menu by its occurrence as well, or
    // a screen reader hears the same name twice.
    var menuLabel = function (row) {
        var same = layout.rows.filter(function (r) { return r.code === row.code; });
        return same.length > 1
            ? intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.subfieldRow.actionsOccurrence' }, { code: "$".concat(row.code), n: same.indexOf(row) + 1 })
            : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.subfieldRow.actions' }, { code: "$".concat(row.code) });
    };
    var renderInsert = function (row) { return (React.createElement(SubfieldCombobox, { id: insertSubfieldComboboxId(pathPrefix, row.key), tag: fieldDef.tag, template: templateOptions, catalog: catalogOptions, autoFocus: true, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.insertPlaceholder' }, { code: "$".concat(row.code) }), label: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.addSubfield.insertCombobox' }, { code: "$".concat(row.code), tag: fieldDef.tag }), onChoose: function (code) {
            setInsertAfter(undefined);
            add(code, row.key);
        }, onDismiss: function (byEscape) {
            var back = insertAfter === null || insertAfter === void 0 ? void 0 : insertAfter.back;
            setInsertAfter(undefined);
            if (byEscape)
                back === null || back === void 0 ? void 0 : back.focus();
        } })); };
    var renderRows = function () { return layout.rows.map(function (row) {
        var sf = row.sf;
        var label = sf ? resolveLabel(sf, intl, row.code) : "$".concat(row.code);
        var marc = !(sf === null || sf === void 0 ? void 0 : sf.formOnly);
        var remove = marc ? removal(row) : undefined;
        return (React.createElement(React.Fragment, { key: row.key },
            React.createElement("div", { style: subfieldHelpRowStyle, "data-subfield-row": row.code, "data-row-key": row.key, onKeyDown: marc ? onRowKeyDown(row.key) : undefined },
                React.createElement("div", { style: { minWidth: 0, flex: 1 } }, row.kind === 'subfield' ? renderSubfield(row.sf) : renderCarried(row)),
                React.createElement("div", { style: subfieldHelpGutterStyle },
                    sf && (React.createElement(SubfieldHelpButton, { tag: fieldDef.tag, subfield: sf, label: label, help: describedHelp(sf) })),
                    (reorderable || insertable || !!remove) && marc && (React.createElement(SubfieldRowMenu, { id: subfieldRowMenuId(pathPrefix, row.key), label: menuLabel(row), canMoveUp: !!moved(row.key, -1), canMoveDown: !!moved(row.key, 1), onMove: function (delta) { return move(row.key, delta); }, onInsert: insertable ? function () { return setInsertAfter({
                            key: row.key,
                            back: document.getElementById(subfieldRowMenuId(pathPrefix, row.key)),
                        }); } : undefined, onRemove: remove })))),
            (insertAfter === null || insertAfter === void 0 ? void 0 : insertAfter.key) === row.key && renderInsert(row)));
    }); };
    // CZNA-718 — the write half of a derived subfield is mounted for EVERY such subfield the
    // field declares, not only the ones this heading type shows. `renderableSubfields` above
    // drops `400 $d` the moment a variant is switched to `ind1=3`, and a value mirrored in
    // while it was `ind1=1` would otherwise survive in form state and be submitted. See
    // `DerivedSubfieldSync`.
    var derivedSubfields = fieldDef.subfields.filter(function (sf) { return sf.derivedFrom; });
    return (React.createElement("div", { ref: panelRef },
        derivedSubfields.map(function (sf) { return (React.createElement(DerivedSubfieldSync, { key: "sync-".concat(sf.code), sf: sf, pathPrefix: pathPrefix, applicable: !sf.hidden && matchesVisibleWhen(sf.visibleWhen, liveInd1) && nameFilled, watch: watch, setValue: setValue })); }),
        renderRows(),
        canAdd && (React.createElement(SubfieldCombobox, { id: addSubfieldMenuId(pathPrefix), tag: fieldDef.tag, template: templateOptions, catalog: catalogOptions, onChoose: function (code) { return add(code); } }))));
};
export default DynamicFieldPanel;
